import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './USP.css';

/* ── Built-in feature icons — composite SVG layers matching Figma custom/32/* ── */
const SvgDelivery = require('../../assets/usp/icon-delivery.svg').ReactComponent;
const SvgPersonal = require('../../assets/usp/icon-personal.svg').ReactComponent;
const SvgWreath = require('../../assets/usp/icon-one.svg').ReactComponent;

const FastDeliveryIcon = () => (
  <SvgDelivery className="ds-usp-block__icon" />
);

const SizeGuaranteeIcon = () => (
  <div className="ds-usp-block__icon ds-usp-block__icon--composite">
    <SvgPersonal className="ds-usp-block__icon-cap" />
  </div>
);

const PaymentIcon = () => (
  <div className="ds-usp-block__icon ds-usp-block__icon--composite">
    <SvgWreath className="ds-usp-block__icon-wreath" />
  </div>
);

/** Built-in icon registry — resolve by string name */
const ICON_MAP = {
  'fast-delivery': FastDeliveryIcon,
  'size-guarantee': SizeGuaranteeIcon,
  payment: PaymentIcon,
};

/**
 * TrustpilotWidget — Real Trustpilot TrustBox embed
 *
 * Loads the official Trustpilot bootstrap script and renders
 * a live widget (Mini template by default).
 *
 * @see https://support.trustpilot.com/hc/en-us/articles/115011421468
 */
const TrustpilotWidget = ({
  businessUnitId,
  locale = 'da-DK',
  templateId = '5419b637fa0340045cd0c936',
  theme = 'light',
  height = '24px',
  width = '100%',
  reviewUrl = '#',
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const loadWidget = () => {
      if (window.Trustpilot && ref.current) {
        window.Trustpilot.loadFromElement(ref.current, true);
      }
    };

    const existing = document.querySelector(
      'script[src*="tp.widget.bootstrap"]'
    );

    if (existing) {
      // Script already on the page — just boot the widget
      if (window.Trustpilot) {
        loadWidget();
      } else {
        existing.addEventListener('load', loadWidget);
      }
      return;
    }

    const script = document.createElement('script');
    script.src =
      'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
    script.async = true;
    script.onload = loadWidget;
    document.head.appendChild(script);
  }, []);

  return (
    <div
      ref={ref}
      className="trustpilot-widget"
      data-locale={locale}
      data-template-id={templateId}
      data-businessunit-id={businessUnitId}
      data-style-height={height}
      data-style-width={width}
      data-theme={theme}
      data-stars="4,5"
    >
      <a href={reviewUrl} target="_blank" rel="noopener noreferrer">
        Trustpilot
      </a>
    </div>
  );
};

TrustpilotWidget.propTypes = {
  businessUnitId: PropTypes.string.isRequired,
  locale: PropTypes.string,
  templateId: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark']),
  height: PropTypes.string,
  width: PropTypes.string,
  reviewUrl: PropTypes.string,
};

/**
 * USP Component
 *
 * Horizontal bar with USP feature items (icon + label) and a
 * Trustpilot widget. Typically placed below the hero section.
 *
 * Figma node: 3872:1612
 *
 * — Mobile:  only Trustpilot widget visible (items hidden)
 * — Desktop (≥ 1024px): items left + Trustpilot right, space-between
 *
 * `width`: 'full' (default) — edge-to-edge grey bar;
 *          'layout' — desktop bar fits the layout width (1360px inside the
 *          gutters, bottom corners rounded) so it hangs under a boxed hero
 *          (Figma 8789:202173). Mobile stays full-bleed in both modes.
 *
 * Uses DS tokens: --color-gray-05 (bg), spacing, typography.
 */
const USP = ({
  items = [],
  trustpilot = null,
  endContent = null,
  width = 'full',
  className = '',
}) => {
  const widthClass = width === 'layout' ? ' ds-usp-block--layout' : '';
  return (
    <section className={`ds-usp-block${widthClass}${className ? ` ${className}` : ''}`}>
      <div className="ds-usp-block__container">
        {items.length > 0 && (
          <div className="ds-usp-block__items">
            {items.map((item, index) => (
              <div key={index} className="ds-usp-block__item">
                <div className="ds-usp-block__item-icon">
                  {typeof item.icon === 'string'
                    ? (ICON_MAP[item.icon] ? React.createElement(ICON_MAP[item.icon]) : null)
                    : item.icon}
                </div>
                <span className="ds-usp-block__item-label typo-body">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="ds-usp-block__end">
          {trustpilot && <TrustpilotWidget {...trustpilot} />}
          {endContent}
        </div>
      </div>
    </section>
  );
};

USP.propTypes = {
  /** Array of USP feature items: { icon: ReactNode | string, label: string }
   *  icon can be a ReactNode or a built-in icon name:
   *  'fast-delivery' | 'size-guarantee' | 'payment'
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ),

  /**
   * Trustpilot config — passed to the built-in TrustpilotWidget.
   * At minimum provide { businessUnitId: '...' }.
   * Set to null to hide.
   */
  trustpilot: PropTypes.shape({
    businessUnitId: PropTypes.string.isRequired,
    locale: PropTypes.string,
    templateId: PropTypes.string,
    theme: PropTypes.oneOf(['light', 'dark']),
    height: PropTypes.string,
    reviewUrl: PropTypes.string,
  }),

  /** Custom end-content slot (rendered after Trustpilot widget) */
  endContent: PropTypes.node,

  /** 'full' — edge-to-edge bar (default); 'layout' — desktop bar fits the layout width */
  width: PropTypes.oneOf(['full', 'layout']),

  className: PropTypes.string,
};

export default USP;
export { TrustpilotWidget };
